import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCurrentUser } from '../../utils/auth';
import { roommateAPI } from '../../utils/api';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Grid,
  Divider,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';

// 预定义的标签
const LIFESTYLE_TAGS = [
  'Early Bird',
  'Night Owl',
  'Clean',
  'Quiet',
  'Social',
  'Pet Friendly',
  'Non-smoker',
  'Vegetarian',
  'Student',
  'Professional',
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const PROPERTY_TYPES = [
  'Apartment',
  'House',
  'Townhouse',
  'Condo'
];

function RoommatePostForm() {
  const navigate = useNavigate();
  const params = useParams();
  const currentUser = getCurrentUser();
  const isLandlord = currentUser?.type === 'landlord';
  const profilePath = isLandlord ? '/landlord/profile' : '/profile';
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    propertyType: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    totalBedrooms: '',
    totalBathrooms: '',
    roommateNeeds: [
      {
        id: 1,
        roomType: 'private', // 'private' (independent room) or 'shared' (shared room with beds)
        bathroomType: 'private' // 'private' or 'shared'
      }
    ],
    availableDate: '',
    selectedTags: [],
    genderPreference: '',
    images: [],
    occupation: {
      isStudent: false,
      school: '',
      isWorking: false,
      workplace: '',
    },
    contactInfo: {
      phone: '',
      email: '',
      preferredContact: 'both' // 'phone', 'email', or 'both'
    },
    signed: false,
  });

  // 新增：存储已上传图片的URL
  const [uploadedImages, setUploadedImages] = useState([]);

  // 检测是否为编辑模式
  useEffect(() => {
    const editId = params.id;
    if (editId) {
      setIsEditMode(true);
      setLoading(true);
      // 模拟加载现有帖子数据
      loadExistingPost(editId);
    }
  }, [params.id]);

  // 编辑模式下加载历史图片
  useEffect(() => {
    if (isEditMode && formData) {
      if (formData.post_images && formData.post_images.length > 0) {
        setUploadedImages(formData.post_images.map(img => ({ url: img.image })));
      } else if (formData.images && formData.images.length > 0) {
        setUploadedImages(formData.images.filter(Boolean).map(url => ({ url })));
      } else {
        setUploadedImages([]);
      }
    }
  }, [isEditMode, formData]);

  const normalizeRoommateNeeds = (raw) => {
    if (!Array.isArray(raw) || raw.length === 0) {
      return [{ id: 1, roomType: 'private', bathroomType: 'private' }];
    }
    return raw.map((need, i) => ({
      id: need.id != null ? need.id : i + 1,
      roomType: need.roomType || need.room_type || 'private',
      bathroomType: need.bathroomType || need.bathroom_type || 'private',
    }));
  };

  // 加载现有帖子数据的函数
  const loadExistingPost = async (id) => {
    try {
      const response = await roommateAPI.getRoommatePost(id);
      const postData = response.data;
      const ci = postData.contact_info || {};
      const occ = postData.occupation || {};

      // 与初始 state 合并，必须带上 contactInfo / occupation，否则整页会因 undefined 崩溃
      setFormData((prev) => ({
        ...prev,
        title: postData.title ?? '',
        description: postData.description ?? '',
        propertyType: postData.property_type ?? '',
        addressLine1: postData.address_line1 ?? '',
        addressLine2: postData.address_line2 ?? '',
        city: postData.city ?? '',
        state: postData.state ?? '',
        zipCode: postData.zip_code ?? '',
        price: postData.price != null ? String(postData.price) : '',
        totalBedrooms: postData.total_bedrooms ?? '',
        totalBathrooms: postData.total_bathrooms ?? '',
        availableDate: postData.available_date ?? '',
        roommateNeeds: normalizeRoommateNeeds(postData.roommate_needs),
        selectedTags: Array.isArray(postData.selected_tags) ? postData.selected_tags : [],
        genderPreference: postData.gender_preference || '',
        images: postData.images || [],
        signed: Boolean(postData.signed),
        post_images: postData.post_images || [],
        contactInfo: {
          ...prev.contactInfo,
          phone: ci.phone ?? '',
          email: ci.email ?? '',
          preferredContact:
            ci.preferredContact ?? ci.preferred_contact ?? prev.contactInfo.preferredContact,
        },
        occupation: {
          ...prev.occupation,
          isStudent: occ.isStudent ?? occ.is_student ?? prev.occupation.isStudent,
          school: occ.school ?? '',
          isWorking: occ.isWorking ?? occ.is_working ?? prev.occupation.isWorking,
          workplace: occ.workplace ?? '',
        },
      }));
    } catch (error) {
      console.error('Error loading post:', error);
      alert('Failed to load post data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOccupationChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      occupation: {
        ...prev.occupation,
        [name]: value,
      },
    }));
  };

  const handleTagToggle = (tag) => {
    setFormData((prev) => {
      const currentTags = prev.selectedTags || [];
      return {
        ...prev,
        selectedTags: currentTags.includes(tag)
          ? currentTags.filter((t) => t !== tag)
          : [...currentTags, tag],
      };
    });
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    for (const file of files) {
      try {
        const res = await roommateAPI.uploadRoommateImage(file);
        const { url } = res.data;
        setUploadedImages((prev) => {
          const next = [...prev, { url }];
          return next;
        });
      } catch (err) {
        alert('Failed to upload image.');
      }
    }
  };

  const handleImageDelete = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleContactInfoChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [name]: value,
      },
    }));
  };

  // 处理室友需求的函数
  const handleRoommateNeedChange = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      roommateNeeds: prev.roommateNeeds.map((need) =>
        need.id === id ? { ...need, [field]: value } : need
      ),
    }));
  };

  const addRoommateNeed = () => {
    const newId = Math.max(...formData.roommateNeeds.map(n => n.id)) + 1;
    setFormData((prev) => ({
      ...prev,
      roommateNeeds: [
        ...prev.roommateNeeds,
        {
          id: newId,
          roomType: 'private',
          bathroomType: 'private'
        }
      ],
    }));
  };

  const removeRoommateNeed = (id) => {
    if (formData.roommateNeeds.length > 1) {
      setFormData((prev) => ({
        ...prev,
        roommateNeeds: prev.roommateNeeds.filter((need) => need.id !== id),
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const images = uploadedImages.map(img => img.url);
      const payload = {
        title: formData.title,
        description: formData.description,
        property_type: formData.propertyType,
        address_line1: formData.addressLine1,
        address_line2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        price: formData.price,
        total_bedrooms: formData.totalBedrooms,
        total_bathrooms: formData.totalBathrooms,
        available_date: formData.availableDate,
        roommate_needs: formData.roommateNeeds,
        selected_tags: formData.selectedTags,
        gender_preference: formData.genderPreference,
        occupation: formData.occupation,
        contact_info: formData.contactInfo,
        images,
        signed: formData.signed,
      };
      if (isEditMode) {
        await roommateAPI.updateRoommatePost(params.id, payload);
        alert('Post updated successfully!');
      } else {
        await roommateAPI.createRoommatePost(payload);
        alert('Post created successfully!');
      }
      navigate(profilePath, { state: { activeTab: 1 } });
    } catch (error) {
      console.error('Submit error:', error);
      const errorMessage = error?.response?.data?.error || 
                          error?.response?.data?.detail || 
                          'Failed to submit. Please check your input.';
      
      if (errorMessage.includes('maximum limit of 2 roommate posts')) {
        alert('You have reached the maximum limit of 2 roommate posts. Please delete an existing post before creating a new one.');
      } else {
        alert(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          {isEditMode ? 'Edit Roommate Listing' : 'Post Roommate Listing'}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading post data...</Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          {/* Required Fields Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 3 }}>
            Required Information
          </Typography>

          {/* Signed Question */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Have you already signed the lease? *
            </Typography>
            <FormControl component="fieldset" required>
              <RadioGroup
                row
                name="signed"
                value={formData.signed ? 'yes' : 'no'}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  signed: e.target.value === 'yes' 
                }))}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Title */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Title *
            </Typography>
            <TextField
              required
              fullWidth
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Give your listing a descriptive title"
              variant="outlined"
            />
          </Box>

          {/* Description */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Description *
            </Typography>
            <TextField
              required
              fullWidth
              multiline
              rows={4}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe what kind of roommate you're looking for"
              variant="outlined"
            />
          </Box>

          {/* Property Type */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Property Type *
            </Typography>
            <FormControl fullWidth required>
              <Select
                name="propertyType"
                value={formData.propertyType}
                onChange={handleInputChange}
                displayEmpty
                variant="outlined"
              >
                <MenuItem value="" disabled>
                  Select property type
                </MenuItem>
                {PROPERTY_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Address Line 1 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Address Line 1 *
            </Typography>
            <TextField
              required
              fullWidth
              name="addressLine1"
              value={formData.addressLine1}
              onChange={handleInputChange}
              placeholder="Street address, P.O. box, company name"
              variant="outlined"
            />
          </Box>

          {/* Address Line 2 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Address Line 2
            </Typography>
            <TextField
              fullWidth
              name="addressLine2"
              value={formData.addressLine2}
              onChange={handleInputChange}
              placeholder="Apartment, suite, unit, building, floor, etc."
              variant="outlined"
            />
          </Box>

          {/* City */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              City *
            </Typography>
            <TextField
              required
              fullWidth
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              placeholder="Enter city name"
              variant="outlined"
            />
          </Box>

          {/* State and ZIP Code */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                State *
              </Typography>
              <FormControl fullWidth required>
                <Select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  displayEmpty
                  variant="outlined"
                >
                  <MenuItem value="" disabled>
                    Select state
                  </MenuItem>
                  {US_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                ZIP Code *
              </Typography>
              <TextField
                required
                fullWidth
                name="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
                placeholder="Enter ZIP code"
                variant="outlined"
              />
            </Grid>
          </Grid>

          {/* Monthly Rent */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Monthly Rent *
            </Typography>
            <TextField
              required
              fullWidth
              name="price"
              type="number"
              value={formData.price}
              onChange={handleInputChange}
              placeholder="Enter monthly rent"
              variant="outlined"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Box>

          {/* Available Date */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Available Date *
            </Typography>
            <TextField
              required
              fullWidth
              name="availableDate"
              type="date"
              value={formData.availableDate}
              onChange={handleInputChange}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>

          {/* Total Property Info */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
            Property Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                Total Bedrooms *
              </Typography>
              <TextField
                required
                fullWidth
                name="totalBedrooms"
                type="number"
                value={formData.totalBedrooms}
                onChange={handleInputChange}
                placeholder="Total bedrooms in property"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                Total Bathrooms *
              </Typography>
              <TextField
                required
                fullWidth
                name="totalBathrooms"
                type="number"
                step="0.5"
                value={formData.totalBathrooms}
                onChange={handleInputChange}
                placeholder="Total bathrooms in property"
                variant="outlined"
              />
            </Grid>
          </Grid>

          {/* Roommate Needs Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
            Roommate Requirements
          </Typography>
          {formData.roommateNeeds.map((need, index) => (
            <Paper
              key={need.id}
              elevation={1}
              sx={{ p: 3, mb: 2, border: '1px solid #e0e0e0' }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Roommate {index + 1}
                </Typography>
                {formData.roommateNeeds.length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => removeRoommateNeed(need.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              
                             <Grid container spacing={2} sx={{ mb: 2 }}>
                 <Grid item xs={6}>
                   <Typography variant="subtitle2" gutterBottom>
                     Room Type *
                   </Typography>
                   <FormControl fullWidth required>
                     <Select
                       value={need.roomType}
                       onChange={(e) => handleRoommateNeedChange(need.id, 'roomType', e.target.value)}
                       variant="outlined"
                     >
                       <MenuItem value="private">Private Room</MenuItem>
                       <MenuItem value="shared">Shared Room (≥2 beds)</MenuItem>
                     </Select>
                   </FormControl>
                 </Grid>
                 <Grid item xs={6}>
                   <Typography variant="subtitle2" gutterBottom>
                     Bathroom Type *
                   </Typography>
                   <FormControl fullWidth required>
                     <Select
                       value={need.bathroomType}
                       onChange={(e) => handleRoommateNeedChange(need.id, 'bathroomType', e.target.value)}
                       variant="outlined"
                     >
                       <MenuItem value="private">Private Bathroom</MenuItem>
                       <MenuItem value="shared">Shared Bathroom</MenuItem>
                     </Select>
                   </FormControl>
                 </Grid>
               </Grid>
            </Paper>
          ))}
          
          <Button
            variant="outlined"
            onClick={addRoommateNeed}
            sx={{ mb: 3 }}
                         startIcon={<AddIcon />}
          >
            Add Another Roommate
          </Button>

          {/* Contact Information Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
            Contact Information
          </Typography>

          {/* Phone Number */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Phone Number
            </Typography>
            <TextField
              fullWidth
              name="phone"
              value={formData.contactInfo.phone}
              onChange={handleContactInfoChange}
              placeholder="Enter your phone number (optional)"
              variant="outlined"
              type="tel"
            />
          </Box>

          {/* Email */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Email Address *
            </Typography>
            <TextField
              required
              fullWidth
              name="email"
              value={formData.contactInfo.email}
              onChange={handleContactInfoChange}
              placeholder="Enter your email address"
              variant="outlined"
              type="email"
            />
          </Box>

          {/* Preferred Contact Method */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Preferred Contact Method *
            </Typography>
            <FormControl fullWidth required>
              <Select
                name="preferredContact"
                value={formData.contactInfo.preferredContact}
                onChange={handleContactInfoChange}
                variant="outlined"
              >
                <MenuItem value="both">Both Phone and Email</MenuItem>
                <MenuItem value="phone">Phone Only</MenuItem>
                <MenuItem value="email">Email Only</MenuItem>
              </Select>
            </FormControl>
          </Box>



          {/* Optional Fields Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
            Additional Information
          </Typography>

          {/* School */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              School
            </Typography>
            <TextField
              fullWidth
              name="school"
              value={formData.occupation.school}
              onChange={handleOccupationChange}
              placeholder="Enter your school name (optional)"
              variant="outlined"
            />
          </Box>

          {/* Workplace */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Workplace
            </Typography>
            <TextField
              fullWidth
              name="workplace"
              value={formData.occupation.workplace}
              onChange={handleOccupationChange}
              placeholder="Enter your workplace (optional)"
              variant="outlined"
            />
          </Box>

          {/* Gender Preference */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Preferred Roommate Gender
            </Typography>
            <TextField
              fullWidth
              name="genderPreference"
              value={formData.genderPreference}
              onChange={handleInputChange}
              placeholder="e.g., male, female, any"
              variant="outlined"
              helperText="You can write anything you want - be creative!"
            />
          </Box>

          {/* Lifestyle Tags */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Lifestyle Preferences
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {LIFESTYLE_TAGS.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onClick={() => handleTagToggle(tag)}
                  color={(formData.selectedTags || []).includes(tag) ? 'primary' : 'default'}
                />
              ))}
            </Box>
          </Box>

          {/* Image Upload */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Photos
            </Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              multiple
              onChange={handleImageUpload}
            />
            <label htmlFor="image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<PhotoCameraIcon />}
              >
                Upload Photos
              </Button>
            </label>
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {uploadedImages.map((img, idx) => (
                <Box
                  key={img.url}
                  sx={{
                    position: 'relative',
                    width: 100,
                    height: 100,
                  }}
                >
                  <img
                    src={img.url}
                    alt={`Uploaded ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 1,
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleImageDelete(idx)}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Submit Button */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate(profilePath, { state: { activeTab: 1 } })}
              sx={{ minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ minWidth: 100 }}
            >
              {isEditMode ? 'Update' : 'Post'}
            </Button>
          </Box>
        </Box>
        )}
      </Paper>
    </Container>
  );
}

export default RoommatePostForm; 